/**
 * Claiming the Age Pension: Services Australia's SA002 (Claim for Age Pension) and the SA369
 * (Income and assets) that goes with it, pre-filled from the profile.
 *
 * Unlike the means-assessment forms these two are fillable PDFs, so the answers go into their own
 * named fields and the client can still type into everything else. The pension is not paid
 * automatically: someone who becomes eligible under a plan has to claim it — online through myGov,
 * or on these two forms. Once it is granted, the residential care means assessment needs only the
 * SA485 (or nothing, for someone without a home), not the SA457.
 *
 * As with every pre-filled form, only what the profile knows is written. Reference numbers,
 * addresses, the bank account for payments, the tax file number, relationship history, travel and
 * residence, and the declarations are the client's.
 */
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { fillAcroForm, openFilledPdf } from './officialFormFill.ts';
import type { PlanFacts } from './planPosition.ts';
import { type Who, num, isCouple, personOf, otherOf, sharesFor, ownsHome, assetsOf, giftsInLastFiveYears, homeLoanBalance, loanBalanceFor, liabilitiesOf } from './profileFacts.ts';

const SA002_PDF = new URL('../../assets/forms/sa002-2608.pdf', import.meta.url).href;
const SA369_PDF = new URL('../../assets/forms/sa369-2606.pdf', import.meta.url).href;
const MODR_PDF = new URL('../../assets/forms/modr-2007.pdf', import.meta.url).href;

export type PensionClaimFormCode = 'SA002' | 'SA369';
export const PENSION_CLAIM_EDITIONS: Record<PensionClaimFormCode, string> = { SA002: 'SA002.2608', SA369: 'SA369.2606' };
export const PENSION_CLAIM_PDFS: Record<PensionClaimFormCode, string> = { SA002: SA002_PDF, SA369: SA369_PDF };

export type AcroValues = Record<string, string | boolean | undefined>;

const dollars = (v: number): string => Math.round(v).toLocaleString('en-AU');
const upper = (s: string): string => s.toUpperCase();
const yn = (b: boolean): 'Yes' | 'No' => (b ? 'Yes' : 'No');

function dmy(dob: string): { d: string; m: string; y: string } | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob);
    return m ? { d: m[3]!, m: m[2]!, y: m[1]! } : null;
}

/** A phone number goes in the mobile box when it looks like one, otherwise the home phone box. */
function phoneBox(phone: string): 'Mobile' | 'HomePhone' {
    return /^(\+?61|0)4/.test(phone.replace(/\s+/g, '')) ? 'Mobile' : 'HomePhone';
}

/** The SA002's "where do you live" options, from the profile's living arrangement. */
const ACCOMMODATION: Record<string, string> = {
    renting: '1',
    own_home: '2',
    aged_care: '7',
    retirement_village: '8',
    with_family: '10',
};

/**
 * The SA002's answers. `claimants` is who is claiming — one person, or both of a couple on the one
 * form. The first claimant fills the "You" column; a second fills "Your partner".
 */
export function sa002Values(ff: DraftFactFind, claimants: Who[]): AcroValues {
    const v: AcroValues = {};
    const couple = isCouple(ff);
    const you = claimants[0] ?? 'client';
    const bothClaim = couple && claimants.length > 1;
    v.Q1 = yn(couple);
    if (couple) {
        v.Q2 = yn(bothClaim);
        if (bothClaim) v.Q3 = 'This form';
    }

    const person = (who: Who, prefix: { name: string; dob: string; gender: string; contact: string }) => {
        const p = personOf(ff, who);
        if (p.last) v[`${prefix.name}.FamilyName`] = upper(p.last);
        if (p.first) v[`${prefix.name}.FirstGivenName`] = upper(p.first);
        const d = dmy(p.dob);
        if (d) { v[`${prefix.dob}.D`] = d.d; v[`${prefix.dob}.M`] = d.m; v[`${prefix.dob}.Y`] = d.y; }
        if (p.sex === 'male') v[prefix.gender] = 'Male';
        if (p.sex === 'female') v[prefix.gender] = 'Female';
        if (p.phone) v[`${prefix.contact}.${phoneBox(p.phone)}`] = p.phone.replace(/\s+/g, '');
        if (p.email) v[`${prefix.contact}.Email_Internet`] = p.email;
    };
    person(you, { name: 'Q5Y', dob: 'Q6Y', gender: 'Q11Y', contact: 'Q14Y' });
    if (bothClaim) person(otherOf(you), { name: 'P.Q5YP', dob: 'P.Q6YP', gender: 'P.Q11YP', contact: 'P.Q14YP' });

    // Living together, and the home they do not live in when one of them is in care.
    const residency = personOf(ff, you).residency;
    if (couple) {
        const apart = residency !== personOf(ff, otherOf(you)).residency && (residency === 'aged_care' || personOf(ff, otherOf(you)).residency === 'aged_care');
        v.Q36 = yn(!apart);
    }
    const inCare = residency === 'aged_care';
    if (ownsHome(ff)) {
        v.Q44 = yn(inCare && (!couple || personOf(ff, otherOf(you)).residency === 'aged_care'));
        if (v.Q44 === 'Yes') v.Q45 = 'Receiving care in a nursing home';
    } else {
        v.Q44 = 'No';
    }
    const accommodation = ACCOMMODATION[residency || (ownsHome(ff) ? 'own_home' : '')];
    if (accommodation) v.Q47 = accommodation;
    return v;
}

/** Income and assets (SA369), from the first claimant's side; a couple's shares are split "you" / "partner". */
export function sa369Values(ff: DraftFactFind, claimants: Who[], asAt = new Date(), facts: PlanFacts = {}): AcroValues {
    const v: AcroValues = {};
    const couple = isCouple(ff);
    const who = claimants[0] ?? 'client';
    const share = (k: { you: string; partner: string }, ownership: unknown) => {
        const s = sharesFor(ownership, who, couple);
        if (s.entity) return;
        v[k.you] = String(s.you);
        if (couple) v[k.partner] = String(s.partner);
    };
    const owner = (key: string, ownership: unknown) => {
        const s = sharesFor(ownership ?? who, who, couple);
        if (s.you >= s.partner && s.you > 0) v[key] = 'You';
        else if (s.partner > 0) v[key] = 'Partner';
    };

    const p = personOf(ff, who);
    if (p.last) v['Q2.FamilyName'] = upper(p.last);
    if (p.first) v['Q2.FirstName'] = upper(p.first);
    const d = dmy(p.dob);
    if (d) { v['Q3.D'] = d.d; v['Q3.M'] = d.m; v['Q3.Y'] = d.y; }
    v.Q4 = yn(couple);
    if (couple) {
        const q = personOf(ff, otherOf(who));
        if (q.last) v['Q6.FamilyName'] = upper(q.last);
        if (q.first) v['Q6.FirstName'] = upper(q.first);
        const qd = dmy(q.dob);
        if (qd) { v['Q7.D'] = qd.d; v['Q7.M'] = qd.m; v['Q7.Y'] = qd.y; }
    }

    const incomes = ff?.income?.otherIncomes || [];
    const whoGets = (ownership: unknown): 'You' | 'Partner' => (sharesFor(ownership ?? 'client', who, couple).you >= 50 ? 'You' : 'Partner');
    const salaries = incomes.filter((i) => i.type === 'salary');
    v.Q8Q = yn(salaries.length > 0);
    salaries.slice(0, 2).forEach((inc, i) => { v[`Q8.PersonWorking${i + 1}`] = whoGets(inc.ownership); });
    v.Q10 = 'No'; // claiming the Age Pension, not Carer Payment, DSP or Farm Household Allowance
    v.Q12 = 'No';

    // Business, private trusts and private companies: the profile's structures.
    const structures = ff?.structures || [];
    v.Q16 = yn(structures.some((x) => x.type === 'partnership'));
    v.Q17 = yn(structures.some((x) => x.type === 'family_trust' || x.type === 'unit_trust'));
    v.Q23 = yn(structures.some((x) => x.type === 'private_company'));

    // Money from outside Australia: the overseas pensions in the profile.
    const overseas = incomes.filter((i) => i.type === 'overseasPension');
    v.Q31Q = yn(overseas.length > 0);
    overseas.slice(0, 2).forEach((inc, i) => {
        const n = i + 1;
        v[`Q31.PaymentType${n}`] = upper(inc.description || 'Overseas pension');
        v[`Q31.PaidTo${n}`] = whoGets(inc.ownership);
    });

    // Boarders and lodgers: the profile's board income, a fortnight, with the meals that come with it.
    const boarders = incomes.filter((i) => i.type === 'boarders');
    v.Q32 = yn(boarders.length > 0);
    boarders.slice(0, 2).forEach((inc, i) => {
        const n = i + 1;
        v[`Q32_Details.Name_${n}`] = upper(inc.description || 'Boarder');
        v[`Q32_Details.NoOfMeals_${n}`] = inc.boardType === 'board' ? '3' : inc.boardType === 'bedAndBreakfast' ? '1' : '0';
        v[`Q32_Details.Amount_${n}`] = dollars(num(inc.amount) / 26);
        share({ you: `Q32_Details.YourShare_${n}`, partner: `Q32_Details.PartnerShare_${n}` }, inc.ownership ?? 'client');
    });

    assetsOf(ff, 'bankAccounts', 'termDeposits').slice(0, 6).forEach((a, i) => {
        const n = i + 1;
        v[`Q35.BankName_${n}`] = upper(a.description || (a.type === 'termDeposits' ? 'Term deposit' : 'Bank account'));
        v[`Q35.Bal_${n}`] = dollars(num(a.value ?? a.balance));
        share({ you: `Q35.YourShare_${n}`, partner: `Q35.PartnerShare_${n}` }, a.ownership);
    });

    // Personal assets: vehicles in their own question, contents and the rest as household contents.
    const personal = assetsOf(ff, 'personalAssets');
    const isBoat = (s: string) => /\b(boat|boats|caravan|caravans|motorhome|motor home|campervan|trailer sailer|yacht|jet ?ski)\b/i.test(s);
    const isVehicle = (s: string) => !isBoat(s) && /\b(car|cars|vehicle|ute|van|truck|motorbike|motorcycle)\b/i.test(s);
    const vehicles = personal.filter((a) => isVehicle(String(a.description || '')));
    const boats = personal.filter((a) => isBoat(String(a.description || '')));
    const contents = personal.filter((a) => !isVehicle(String(a.description || '')) && !isBoat(String(a.description || '')));
    const contentsTotal = contents.reduce((t, a) => t + num(a.value), 0);
    if (contentsTotal > 0) {
        v['Q36.Amount'] = dollars(contentsTotal);
        share({ you: 'Q36.YourShare', partner: 'Q36PartnerShare' }, contents[0]!.ownership);
    }
    v.Q37Q = yn(vehicles.length > 0);
    vehicles.slice(0, 2).forEach((a, i) => {
        const n = i + 1;
        v[`Q37.AssetType_${n}`] = upper(a.description || 'Car');
        v[`Q37.MValue_${n}`] = dollars(num(a.value));
        share({ you: `Q37.YourShare_${n}`, partner: `Q37.PartnerShare_${n}` }, a.ownership);
    });
    v.Q38Q = yn(boats.length > 0);
    boats.slice(0, 2).forEach((a, i) => {
        const n = i + 1;
        v[`Q38.AssetTYpe_${n}`] = upper(a.description || 'Boat or caravan');
        v[`Q38.MValue_${n}`] = dollars(num(a.value));
        share({ you: `Q38.YourShare_${n}`, partner: `Q38.PartnerShare_${n}` }, a.ownership);
    });

    const home = ff?.home || {};
    v.Q39 = yn(ownsHome(ff));
    if (ownsHome(ff)) {
        v.Q40 = yn(!!home.landSize);
        v.Q41 = yn(!!home.separateTitles);
        v.Q42 = yn(!!home.businessUse);
    }
    const ips = assetsOf(ff, 'investmentProperty');
    // A former home let out while living in a village is other real estate too (planPosition.ts).
    const otherProperties = ips.length + (facts.homeRented && !facts.enteringCare ? 1 : 0);
    v.Q43 = yn(otherProperties > 0);
    if (otherProperties) v.Q44 = String(otherProperties);

    /*
     * Q33, a former home let out after entering care: answered under a plan that does it. Without
     * a plan it is "No" for someone not in care, and left for the client otherwise.
     */
    if (facts.enteringCare) v.Q33 = yn(!!facts.homeRented);
    else if (personOf(ff, who).residency !== 'aged_care') v.Q33 = 'No';

    const supers = assetsOf(ff, 'superannuation');
    v.Q45Q = yn(supers.length > 0);
    supers.slice(0, 2).forEach((a, i) => {
        const n = i + 1;
        v[`Q45.ManagerName_${n}`] = upper(a.description || 'Superannuation');
        v[`Q45.MValue_${n}`] = dollars(num(a.value));
        owner(`Q45.Ownedby_${n}`, a.ownership);
    });
    const streams = assetsOf(ff, 'accountBasedPensions', 'annuities');
    v.Q46Q = yn(streams.length > 0);
    streams.slice(0, 2).forEach((a, i) => {
        const n = i + 1;
        v[`Q46.ProductProvider_${n}`] = upper(a.description || (a.type === 'annuities' ? 'Annuity' : 'Account-based pension'));
        v[`Q46.Type_${n}`] = a.type === 'annuities' ? 'ANNUITY' : 'ACCOUNT-BASED PENSION';
        owner(`Q46.Ownedby_${n}`, a.ownership);
    });

    const shares = assetsOf(ff, 'shares');
    v.Q47Q = yn(shares.length > 0);
    if (!shares.length) { v.Q48Q = 'No'; v.Q49Q = 'No'; v.Q50Q = 'No'; }
    v.Q51Q = 'No'; // government bonds and debentures: not something the profile records

    shares.slice(0, 4).forEach((a, i) => {
        const n = i + 1;
        v[`Q47.CompanyName_${n}`] = upper(a.description || 'Share portfolio');
        share({ you: `Q47.YourShare_${n}`, partner: `Q47.PartnerShare_${n}` }, a.ownership);
    });

    // Money on loan: who, when, how much, what is still owed, and whose.
    const loans = assetsOf(ff, 'loansToOthers');
    v.Q52Q = yn(loans.length > 0);
    loans.slice(0, 2).forEach((a, i) => {
        const n = i + 1;
        v[`Q52.WhoTo_${n}`] = upper(a.description || 'Loan');
        const dd = dmy(String(a.commencementDate || ''));
        if (dd) { v[`Q52.DateLent.${n}.D`] = dd.d; v[`Q52.DateLent.${n}.M`] = dd.m; v[`Q52.DateLent.${n}.Y`] = dd.y; }
        if (num(a.purchasePrice) > 0) v[`Q52.Amount_${n}`] = dollars(num(a.purchasePrice));
        v[`Q52.Balance_${n}`] = dollars(num(a.value));
        share({ you: `Q52.You_${n}`, partner: `Q52.Partner_${n}` }, a.ownership);
    });

    const liabilities = Object.values((ff?.liabilities || {}) as Record<string, Array<{ type?: string; id?: string; description?: string }>>).flat();
    const reverse = liabilities.filter((l) => l && (l.type === 'reverse_mortgage' || l.id === 'reverse-mortgage'));
    v.Q53Q = yn(reverse.length > 0);

    const gifts = giftsInLastFiveYears(ff, asAt);
    if (gifts.length) v.Q54Q = 'Yes';
    else if (ff?.giftsHistory?.confirmedNone) v.Q54Q = 'No';
    // The profile records gifts by financial year, so the year stands in for the date.
    gifts.slice(0, 3).forEach((g, i) => {
        const n = i + 1;
        v[`Q54.Given_${n}`] = `MONEY (GIFTS IN ${g.year - 1}-${String(g.year).slice(2)})`;
        v[`Q54.Worth_${n}`] = dollars(g.amount);
        v[`Q54.AmountReceived_${n}`] = '0';
        v[`Q54.SDT_${n}`] = 'No';
    });

    // Q55 covers funeral bonds and pre-paid funerals; Q56 asks again about a contract already paid for.
    const funeral = assetsOf(ff, 'funeralBonds', 'prepaidFuneral');
    v.Q55Q = yn(funeral.length > 0);
    v.Q56 = yn(funeral.some((a) => a.type === 'prepaidFuneral'));
    funeral.slice(0, 2).forEach((a, i) => {
        const n = i + 1;
        v[`Q55.CompanyName_${n}`] = upper(a.description || (a.type === 'prepaidFuneral' ? 'Prepaid funeral' : 'Funeral bond'));
        v[`Q55.Value_${n}`] = dollars(num(a.value));
        share({ you: `Q55.YourShare_${n}`, partner: `Q55.PartnerShare_${n}` }, a.ownership ?? who);
    });

    const bonds = assetsOf(ff, 'investmentBonds');
    v.Q57Q = yn(bonds.length > 0);
    bonds.slice(0, 2).forEach((a, i) => {
        const n = i + 1;
        v[`Q57.InsuranceCoName_${n}`] = upper(a.description || 'Investment bond');
        v[`Q57.SurrenderValue_${n}`] = dollars(num(a.value));
        share({ you: `Q57.YourShare_${n}`, partner: `Q57.PartnerShare_${n}` }, a.ownership);
    });

    const payments = incomes.filter((i) => i.type === 'dvaWarWidow' || i.type === 'dvaDisability' || i.type === 'other');
    v.Q58Q = yn(payments.length > 0);
    payments.slice(0, 2).forEach((inc, i) => {
        const n = i + 1;
        const dva = inc.type === 'dvaWarWidow' || inc.type === 'dvaDisability';
        v[`Q58.Type_${n}`] = upper(inc.description || ({ dvaWarWidow: "DVA war widow(er)'s pension", dvaDisability: 'DVA disability pension', other: 'Other income' } as Record<string, string>)[String(inc.type)] || 'Income');
        if (dva) v[`Q58.WhoPays_${n}`] = "DEPARTMENT OF VETERANS' AFFAIRS";
        v[`Q58.Amount_${n}`] = dollars(num(inc.amount) / 26);
        share({ you: `Q58.YourShare_${n}`, partner: `Q58.PartnerShare_${n}` }, inc.ownership ?? 'client');
    });

    const other = assetsOf(ff, 'other');
    v.Q59Q = yn(other.length > 0);
    other.slice(0, 2).forEach((a, i) => {
        const n = i + 1;
        v[`Q59.Asset_${n}`] = upper(a.description || 'Other asset');
        v[`Q59.CurrentValue_${n}`] = dollars(num(a.value));
        share({ you: `Q59.YourShare_${n}`, partner: `Q59.PartnerShare_${n}` }, a.ownership);
    });

    /*
     * Question 60, the checklist of what goes with the form: ticked for every "Yes" above that asks
     * for a document. The boxes run down the printed list in order (Q601 to Q6030).
     */
    const docFor: Array<[number, boolean]> = [
        [1, v.Q8Q === 'Yes'],
        [6, v.Q16 === 'Yes'],
        [16, v.Q31Q === 'Yes'],
        [17, v.Q33 === 'Yes' || v.Q40 === 'Yes' || v.Q41 === 'Yes' || v.Q42 === 'Yes' || v.Q43 === 'Yes'],
        [18, v.Q45Q === 'Yes'],
        [19, v.Q46Q === 'Yes'],
        [20, v.Q47Q === 'Yes'],
        [25, v.Q52Q === 'Yes'],
        [26, v.Q53Q === 'Yes'],
        [27, v.Q55Q === 'Yes'],
        [28, v.Q56 === 'Yes'],
        [29, v.Q57Q === 'Yes'],
        [30, v.Q58Q === 'Yes'],
    ];
    for (const [k, on] of docFor) if (on) v[`Q60${k}`] = 'Yes';
    return v;
}

export interface PensionClaimFillOptions {
    /** Shown on the form's properties so a filled copy says which position it was filled from. */
    positionLabel: string;
    afterPlan?: boolean;
    /** What the plan does that the profile cannot record, such as letting the home. */
    facts?: PlanFacts;
}

/** Fetch the claim form, fill it, and hand it to the client. */
export async function openPensionClaimForm(code: PensionClaimFormCode, ff: DraftFactFind, claimants: Who[], opts: PensionClaimFillOptions): Promise<void> {
    const bytes = await (await fetch(PENSION_CLAIM_PDFS[code])).arrayBuffer();
    const values = code === 'SA002' ? sa002Values(ff, claimants) : sa369Values(ff, claimants, new Date(), opts.facts);
    const filled = await fillAcroForm(bytes, values, {
        title: code === 'SA002' ? 'Claim for Age Pension (SA002) — pre-filled' : 'Income and assets (SA369) — pre-filled',
        subject: `${PENSION_CLAIM_EDITIONS[code]}, pre-filled from the client profile (${opts.positionLabel}). Source: servicesaustralia.gov.au (CC BY 4.0).`,
    }, { revealHidden: true });
    const name = personOf(ff, claimants[0] ?? 'client').first || 'Client';
    openFilledPdf(filled, `${code}_${name}_${opts.afterPlan ? 'after-plan' : 'prefilled'}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

/* ─── Real estate details (Mod R) ─────────────────────────────────────────────────────────────── */

/**
 * The properties the SA369 sends to a Mod R, one form each: every investment property, and the home
 * when it is over 2 hectares, on more than one title or partly used for business (SA369 questions
 * 40 to 44). A property owned by a private trust or company is not one: that goes on a Mod PT or
 * Mod PC instead, as the Mod R's own first question says.
 */
export type ModRProperty = { kind: 'home' } | { kind: 'investment'; index: number; description: string };

export function modRProperties(ff: DraftFactFind, facts: PlanFacts = {}): ModRProperty[] {
    const out: ModRProperty[] = [];
    const home = ff?.home || {};
    // A let former home needs one too (SA369 Q33, or Q43 from a village).
    if (ownsHome(ff) && (home.landSize || home.separateTitles || home.businessUse || facts.homeRented) && !String(home.ownership || '').startsWith('entity')) out.push({ kind: 'home' });
    assetsOf(ff, 'investmentProperty').forEach((a, index) => {
        if (String(a.ownership || '').startsWith('entity:') || a.ownership === 'other_entity') return;
        out.push({ kind: 'investment', index, description: String(a.description || `Property ${index + 1}`) });
    });
    return out;
}

const slashDate = (iso: unknown): string | undefined => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : undefined;
};

/** One property's Mod R, from the first claimant's side. */
export function modRValues(ff: DraftFactFind, property: ModRProperty, claimants: Who[], facts: PlanFacts = {}): AcroValues {
    const v: AcroValues = {};
    const couple = isCouple(ff);
    const who = claimants[0] ?? 'client';
    const p = personOf(ff, who);
    v.Q1 = 'No';
    if (p.last) v.Q3FamilyName = upper(p.last);
    if (p.first) v.Q3FirstName = upper(p.first);
    v.Q4DateOfBirth = slashDate(p.dob);
    const ips = assetsOf(ff, 'investmentProperty');
    v.Q5 = String(ips.length + (ownsHome(ff) ? 1 : 0));

    let ownership: unknown;
    let loan = 0;
    let loanRate: number | undefined;
    if (property.kind === 'home') {
        const home = ff?.home || {};
        ownership = home.ownership;
        if (home.landSize) v.Q7 = '2ha';
        else if (facts.homeRented) v.Q7 = 'House';
        v['Q9.CurValue'] = dollars(num(home.value));
        // Let out under the plan: nobody in the household lives there any more.
        v.Q11 = facts.homeRented ? 'No' : 'Yes';
        if (facts.homeRented) {
            v.Q36 = facts.homeRented.perWeek > 0 ? 'Yes' : 'No';
            if (facts.homeRented.perWeek > 0) v['Q36Details.GrossRent'] = dollars(facts.homeRented.perWeek);
        }
        v.Q12 = yn(!!home.landSize);
        if (home.landSize && num(home.landSizeValue) > 0) v.Q13 = dollars(Math.max(0, num(home.value) - num(home.landSizeValue)));
        v.Q14 = yn(!!home.businessUse);
        if (home.businessUse && num(home.businessUseValue) > 0) v.Q15 = dollars(num(home.businessUseValue));
        v.Q16 = yn(!!home.primaryProductionUse);
        v.Q18 = yn(!!home.separateTitles);
        if (home.landSize) {
            v.Q20 = 'Yes'; // completed with an Age Pension claim
            if (home.longTermLandAttachment !== undefined) v.Q21 = yn(!!home.longTermLandAttachment);
        }
        loan = homeLoanBalance(ff);
        loanRate = liabilitiesOf(ff).find((l) => l.type === 'mortgages' && (!l.assetLink || l.assetLink === 'home'))?.interestRate;
    } else {
        const a = ips[property.index]!;
        ownership = a.ownership;
        v['Q9.CurValue'] = dollars(num(a.value));
        v.Q11 = 'No';
        if (num(a.purchasePrice) > 0) { v.Q29 = 'Purchased'; v['Q29Details.Price'] = dollars(num(a.purchasePrice)); }
        v['Q30.OwnershipDate'] = slashDate(a.acquiredOn);
        loan = loanBalanceFor(ff, a.id);
        loanRate = liabilitiesOf(ff).find((l) => String(l.assetLink) === String(a.id))?.interestRate;
        v.Q36 = yn(num(a.rent) > 0);
        if (num(a.rent) > 0) v['Q36Details.GrossRent'] = dollars(num(a.rent) / 52);
    }

    const s = sharesFor(ownership, who, couple);
    if (s.you > 0) { v['Q28.0'] = 'Yes'; v.Q28Percent1 = String(s.you); }
    if (couple && s.partner > 0) { v['Q28.1'] = 'Yes'; v.Q28Percent2 = String(s.partner); }
    if (s.other > 0) { v['Q28.2'] = 'Yes'; v.Q28Percent3 = String(s.other); }

    v.Q33 = yn(loan > 0);
    if (loan > 0) {
        v['Q35.Owe'] = dollars(loan);
        if (num(loanRate) > 0) v['Q35.Interest'] = String(num(loanRate));
    }

    // The checklist: a Mod R for each property, the rates notice it always asks for, and the loan and rental papers.
    if (num(v.Q5) > 1) v['Q43.0'] = 'Yes';
    v['Q43.1'] = 'Yes';
    if (v.Q18 === 'Yes') v['Q43.3'] = 'Yes';
    if (v.Q33 === 'Yes') v['Q43.5'] = 'Yes';
    if (v.Q36 === 'Yes') v['Q43.6'] = 'Yes';
    return v;
}

export function modRLabel(property: ModRProperty): string {
    return property.kind === 'home' ? 'your home' : property.description;
}

/** One Mod R, filled for one property. */
export async function openModRForm(ff: DraftFactFind, property: ModRProperty, claimants: Who[], opts: PensionClaimFillOptions): Promise<void> {
    const bytes = await (await fetch(MODR_PDF)).arrayBuffer();
    const filled = await fillAcroForm(bytes, modRValues(ff, property, claimants, opts.facts), {
        title: `Real estate details (Mod R) — ${modRLabel(property)} — pre-filled`,
        subject: `Mod(R).2007, pre-filled from the client profile (${opts.positionLabel}). Source: servicesaustralia.gov.au (CC BY 4.0).`,
    }, { revealHidden: true });
    const stem = modRLabel(property).replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30) || 'property';
    openFilledPdf(filled, `ModR_${stem}_${opts.afterPlan ? 'after-plan' : 'prefilled'}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
